import { useCallback, useContext } from "react";

import {
  CodeEditor,
  dollarInvoker,
} from "src/base-components/CodeInput/CodeEditor";
import { AiNodeV2Data } from "src/constants/NodeDataTypes";
import { Tooltip } from "src/design-system/Tooltip";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import {
  NodeEditorBaseProps,
  NodeEditorCompletionContext,
} from "src/nodeEditor/NodeEditor";

type CustomInvokeCodeEditorProps = {
  value: string;
  placeholder?: string;
  errorMessage?: string;
  searchHighlightKey?: string;
  disableCompletion?: boolean;
  onChange: (value: string | undefined) => void;
} & Omit<NodeEditorBaseProps<AiNodeV2Data>, "onUpdate" | "displayedError">;

export const CustomInvokeCodeEditor: React.FC<CustomInvokeCodeEditorProps> = ({
  value,
  onChange,
  errorMessage,
  immutable,
  isReactive,
  searchHighlightKey,
  placeholder,
  disableCompletion,
}) => {
  const { defaultCompletion } = useContext(NodeEditorCompletionContext);
  const isSearchHighlighted = useIsFieldHighlighted(searchHighlightKey);

  const basicSetup = {
    lineNumbers: false,
    highlightActiveLine: false,
  };

  const onValueChange = useCallback(
    (newValue: string | undefined) => {
      if (!isReactive && value !== newValue) {
        onChange(newValue ?? "");
      }
    },
    [isReactive, value, onChange],
  );

  return (
    <Tooltip
      disabled={!errorMessage}
      placement="top"
      title={errorMessage}
      asChild
    >
      <div className="h-full w-full">
        <CodeEditor
          backgroundColor="white"
          basicSetup={basicSetup}
          completionExtension={
            !disableCompletion && defaultCompletion
              ? [defaultCompletion, dollarInvoker.extension]
              : undefined
          }
          dataLoc="prompt-editor"
          disabled={immutable}
          drawErrorBorder={!!errorMessage}
          fontFamily="inter"
          isSearchHighlighted={isSearchHighlighted}
          language="prompt"
          placeholder={placeholder}
          value={value}
          lineWrapping
          onChange={onValueChange}
        />
      </div>
    </Tooltip>
  );
};
